import React, { useState } from 'react';
import { RefreshCw, Package, Droplet, ChevronRight, ListChecks } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import DetalleSolicitudModal from '@/components/solicitudes/DetalleSolicitudModal';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';

const SolicitudesPendientes = ({ solicitudes, isLoading, onRefresh, onRefreshAlistadas }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [alistandoId, setAlistandoId] = useState(null);

  const handleVerDetalle = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setIsModalOpen(true);
  };

  const handleAlistarExtraRuta = async (e, solicitud) => {
    e.stopPropagation(); // Evitar que se abra el modal
    
    setAlistandoId(solicitud._id);
    try {
      // Crear entrega directa en estado "alistado"
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${API_URL}/entregas-lenceria/crear-directa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          edificio: {
            id: solicitud.edificio?.id || solicitud.edificio?._id,
            nombre: solicitud.edificio?.nombre,
            ciudad: solicitud.edificio?.ciudad,
            direccion: solicitud.edificio?.direccion
          },
          entregador: {
            id: user._id,
            nombre: user.name || user.nombre
          },
          items: solicitud.items?.map(item => ({
            id: item.id,
            nombre: item.nombre,
            cantidad: item.cantidad,
            peso: item.peso,
            tipo: 'lenceria'
          })) || [],
          peso_total: solicitud.peso_total || 0,
          observaciones: `Extra ruta - Solicitud: ${solicitud._id}`,
          tipo_entrega: 'directa',
          solicitud_id: solicitud._id,
          hospedaje: solicitud.hospedaje,
          nombrePropiedad: solicitud.nombrePropiedad
        }),
      });

      const result = await response.json();

      if (response.ok && result.data) {
        // Actualizar estado de la solicitud a "Alistado"
        const updateResponse = await fetch(`${API_URL}/solicitud-prendas/actualizar-estado`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: solicitud._id,
            estado: 'Alistado'
          }),
        });

        if (!updateResponse.ok) {
          console.warn('No se pudo actualizar el estado de la solicitud');
        }

        toast({
          title: 'Éxito',
          description: 'Entrega alistada correctamente. Aparecerá en "Alistadas" para completar después.',
          duration: 3000,
        });
        
        // Recargar solicitudes pendientes y entregas alistadas
        if (onRefresh) {
          await onRefresh();
        }
        if (onRefreshAlistadas) {
          await onRefreshAlistadas();
        }
      } else {
        throw new Error(result.message || 'Error al crear entrega');
      }
    } catch (error) {
      console.error('Error al alistar entrega extra ruta:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo alistar la entrega',
        variant: 'destructive',
      });
    } finally {
      setAlistandoId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-20 bg-gray-200 rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (solicitudes.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">No hay solicitudes pendientes</p>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Pull to refresh hint */}
      <div className="text-center text-xs text-gray-400 mb-2">
        Desliza hacia abajo para actualizar
      </div>

      {solicitudes.map((solicitud) => {
        const tipoIcon = solicitud.tipo === 'lenceria' ? Package : Droplet;
        const TipoIcon = tipoIcon;
        const isExtraRuta = solicitud.isExtraRuta;

        return (
          <Card 
            key={solicitud._id} 
            className={`hover:shadow-md transition-all cursor-pointer active:scale-[0.98] ${
              isExtraRuta ? 'border-2 border-red-500 shadow-lg shadow-red-100' : ''
            }`}
            onClick={() => handleVerDetalle(solicitud)}
          >
            <CardContent className={`p-4 ${
              isExtraRuta ? 'bg-red-50/30' : ''
            }`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 flex-1">
                  <TipoIcon className={`h-5 w-5 flex-shrink-0 ${
                    solicitud.tipo === 'lenceria' ? 'text-blue-600' : 'text-purple-600'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm">
                      {solicitud.edificio?.nombre || 'Sin edificio'} 
                    </h3>
                    {solicitud.hospedaje?.nombre && (
                      <p className="text-xs text-gray-600 truncate">
                        {solicitud.hospedaje.nombre}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      {solicitud.edificio?.ciudad || 'Sin ciudad'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isExtraRuta ? (
                    <Badge className="bg-red-100 text-red-700 text-xs border-red-300">
                      🚨 Extra Ruta 
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-orange-100 text-orange-700 text-xs">
                      Pendiente
                    </Badge>
                  )}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </div>

            <div className="grid grid-cols-3 gap-2 text-xs mb-3">
              <div className="bg-gray-50 rounded p-2 text-center">
                <div className="text-gray-500">Items</div>
                <div className="font-semibold text-gray-900">
                  {solicitud.total_solicitado || 0}
                </div>
              </div>
              <div className="bg-gray-50 rounded p-2 text-center">
                <div className="text-gray-500">Peso</div>
                <div className="font-semibold text-gray-900">
                  {((solicitud.peso_total || 0) / 1000).toFixed(1)} kg
                </div>
              </div>
              <div className="bg-gray-50 rounded p-2 text-center">
                <div className="text-gray-500">Tipo</div>
                <div className="font-semibold text-gray-900 capitalize">
                  {solicitud.tipo || 'N/A'}
                </div>
              </div>
            </div>

              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-gray-400">
                  {new Date(solicitud.createdAt).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                  {solicitud.fechaEntrega && (
                    <span className="ml-2 border-l pl-2 font-bold text-gray-600">Entrega: {new Date(solicitud.fechaEntrega).toLocaleDateString('es-ES', {
                      day: '2-digit',
                      month: 'short',
                    })}</span>
                  )}
                </div>
                
          {/*       {isExtraRuta && (
                  <Button
                    size="sm"
                    variant="default"
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 px-3"
                    onClick={(e) => handleAlistarExtraRuta(e, solicitud)}
                    disabled={alistandoId === solicitud._id}
                  >
                    {alistandoId === solicitud._id ? (
                      <RefreshCw className="h-3 w-3 animate-spin" />
                    ) : (
                      <>
                        <ListChecks className="h-3 w-3 mr-1" />
                        Alistar
                      </>
                    )}
                  </Button>
                )} */}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Modal de detalle */}
      <DetalleSolicitudModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedSolicitud(null);
        }}
        solicitud={selectedSolicitud}
        tipo={selectedSolicitud?.tipo || 'lenceria'}
      />
    </div>
  );
};

export default SolicitudesPendientes;
